import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ASSETS, MODULES, TIERS, FAQ, valueOfStack, api } from "../lib/brand";
import { captureAndGetUtm } from "../lib/utm";
import LeadMagnetForm from "../components/LeadMagnetForm";
import ExitIntentModal from "../components/ExitIntentModal";

const SectionLabel = ({ children }) => <span className="haven-label">{children}</span>;

const Hero = () => {
  return (
    <section data-testid="hero-section" className="relative haven-grain">
      <div className="haven-page-shell-wide" style={{ paddingTop: 56, paddingBottom: 32 }}>
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <SectionLabel>Founding Cohort · 500 seats</SectionLabel>
            <h1
              className="font-serif mt-4"
              style={{
                fontSize: "clamp(40px, 6.4vw, 76px)",
                lineHeight: 1.02,
                color: "var(--ink)",
                letterSpacing: "-0.01em",
              }}
            >
              Your machine isn't broken.
              <br />
              <em style={{ color: "var(--gold)", fontStyle: "italic" }}>You just haven't been taught</em>
              <br />
              how it actually works.
            </h1>
            <p
              className="mt-6 max-w-xl"
              style={{ color: "var(--ink-soft)", fontSize: 16, lineHeight: 1.65 }}
            >
              30 years of hands-on technician work, structured into a curriculum, paired with diagnostic
              studio tools and your personal sewing experts built on Daniel Hanson's framework. Member-funded. No investors.
              No tutorials. <span style={{ color: "var(--ink)" }}>Teaching.</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3 fade-up delay-2">
              <Link to="/join" data-testid="hero-cta-primary" className="haven-btn haven-btn-primary">
                Lock founding rate · from $29/mo
              </Link>
              <Link to="/diagnose" data-testid="hero-cta-secondary" className="haven-btn haven-btn-ghost">
                Try the AI tension diagnostic
              </Link>
            </div>
            <div className="mt-7 flex items-center gap-4 flex-wrap fade-up delay-3">
              <span className="haven-bubble" data-testid="hero-savings-bubble">$20/mo savings · locked permanently</span>
              <span style={{ color: "var(--ink-muted)", fontSize: 12 }}>
                or save up to <b style={{ color: "var(--ink)" }}>$2,000</b> with a one-time lifetime payment
              </span>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up delay-2">
            <div
              className="haven-card"
              style={{ padding: 0, overflow: "hidden", borderRadius: 22 }}
              data-testid="hero-video-card"
            >
              <div
                data-testid="hero-explainer-video"
                style={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "16 / 9",
                  background: "#000",
                  backgroundImage: "url('https://i.ytimg.com/vi/2zuny5QZnDk/maxresdefault.jpg')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <iframe
                  src="https://www.youtube-nocookie.com/embed/2zuny5QZnDk?rel=0&modestbranding=1&playsinline=1"
                  title="The Sewing Haven — Founder Explainer"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                    display: "block",
                  }}
                />
              </div>
              <div style={{ padding: "16px 18px", borderTop: "1px solid var(--border-soft)" }}>
                <div className="haven-label">From the founder</div>
                <p className="font-serif italic" style={{ color: "var(--ink)", fontSize: 18, lineHeight: 1.4, marginTop: 4 }}>
                  "78% of machine problems are knowledge gaps."
                </p>
                <span style={{ color: "var(--ink-muted)", fontSize: 11 }}>— Daniel Hanson, certified at 11</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="haven-divider-grain" />
    </section>
  );
};

const Problem = () => (
  <section data-testid="problem-section" className="haven-page-shell-wide" style={{ paddingTop: 64, paddingBottom: 32 }}>
    <div className="max-w-2xl">
      <SectionLabel>The Gap</SectionLabel>
      <h2 className="font-serif mt-3" style={{ fontSize: "clamp(30px, 4vw, 44px)", lineHeight: 1.1, color: "var(--ink)" }}>
        YouTube shows you what. Nobody shows you why.
      </h2>
      <p className="mt-4" style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.7 }}>
        You've Googled the same tension problem six different ways. You've taken your machine to the shop and gotten
        it back "fixed" with no explanation. You've watched 90 minutes of videos and still don't understand why your
        thread keeps shredding. The gap isn't your skill — it's the missing layer between the technician's bench and
        the educator's camera.
      </p>
    </div>
    <div className="grid md:grid-cols-3 gap-5 mt-10">
      {[
        { num: "78%", text: "of machine problems are knowledge gaps, not broken parts." },
        { num: "1×", text: "explanation per service call — most shops fix and hand it back without the why." },
        { num: "5+", text: "new videos per month answering real member questions, on Daniel's framework." },
      ].map((b, i) => (
        <div key={i} className="haven-card fade-up" style={{ padding: 22 }}>
          <div className="font-serif" style={{ fontSize: 44, color: "var(--gold)", lineHeight: 1 }}>{b.num}</div>
          <p style={{ marginTop: 10, color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.55 }}>{b.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const Modules = () => (
  <section data-testid="modules-section" className="haven-page-shell-wide" style={{ paddingTop: 48, paddingBottom: 48 }}>
    <div className="flex items-end justify-between flex-wrap gap-4 mb-7">
      <div>
        <SectionLabel>The Stack · 16 modules</SectionLabel>
        <h2 className="font-serif mt-3" style={{ fontSize: "clamp(30px, 4vw, 44px)", lineHeight: 1.1, color: "var(--ink)" }}>
          The studio you'd build if you owned every tool.
        </h2>
      </div>
      <div className="haven-card haven-card-gold" style={{ padding: "12px 18px" }}>
        <div className="haven-label">À la carte value</div>
        <div className="font-serif" style={{ fontSize: 28, color: "var(--ink)", lineHeight: 1 }}>
          ${valueOfStack}+
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>
          Bundled in founding from <b style={{ color: "var(--ink)" }}>$29/mo</b>
        </div>
      </div>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {MODULES.map((m, i) => {
        const isStitchOut = m.name === "Stitch-Out Simulator";
        const inner = (
          <>
            <div className="flex items-center justify-between">
              <span className="haven-bubble" style={{ background: "var(--sage-light)", color: "var(--ink)" }}>{m.group}</span>
              <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>${m.standalone} stand-alone</span>
            </div>
            <h3 className="font-serif mt-3" style={{ fontSize: 22, color: "var(--ink)" }}>
              {m.name}
              {isStitchOut && (
                <span style={{ marginLeft: 8, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--gold)", verticalAlign: "middle" }}>
                  · try the calculator
                </span>
              )}
            </h3>
            <p style={{ marginTop: 8, color: "var(--ink-soft)", fontSize: 13.5, lineHeight: 1.6 }}>{m.copy}</p>
            {isStitchOut && (
              <div style={{ marginTop: 12, fontSize: 12, color: "var(--gold)", letterSpacing: "0.04em" }}>
                See your annual loss in 60 seconds →
              </div>
            )}
          </>
        );
        if (isStitchOut) {
          return (
            <Link
              key={m.name}
              to="/embroidery-cost"
              data-testid={`module-card-${i}`}
              className="haven-card fade-up"
              style={{
                padding: 20,
                textDecoration: "none",
                border: "1px solid var(--gold-light)",
                background: "linear-gradient(180deg, var(--gold-faint) 0%, var(--surface-card) 75%)",
              }}
            >
              {inner}
            </Link>
          );
        }
        return (
          <div key={m.name} data-testid={`module-card-${i}`} className="haven-card fade-up" style={{ padding: 20 }}>
            {inner}
          </div>
        );
      })}
    </div>
  </section>
);

const Daniel = () => (
  <section data-testid="daniel-section" className="haven-page-shell-wide" style={{ paddingTop: 48, paddingBottom: 48 }}>
    <div className="grid md:grid-cols-12 gap-8 items-center">
      <div className="md:col-span-5 fade-up">
        <div
          className="haven-card"
          style={{ padding: 0, overflow: "hidden", borderRadius: 22 }}
        >
          <img
            src={ASSETS.danielPortrait}
            alt="Daniel Hanson, founder of The Sewing Haven, in front of a quilt"
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </div>
      </div>
      <div className="md:col-span-7 fade-up delay-1">
        <SectionLabel>Meet Daniel</SectionLabel>
        <h2 className="font-serif mt-3" style={{ fontSize: "clamp(28px, 3.6vw, 40px)", lineHeight: 1.1, color: "var(--ink)" }}>
          Sewing since five. Servicing since six. Certified at eleven.
        </h2>
        <p className="mt-4" style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.7 }}>
          Daniel grew up in his family's sew &amp; vac shop. By eleven he was a fully certified technician, by thirteen
          selling and educating, and over the next two decades he became the tech other techs called when they got
          stuck. He won Janome's national sales competition — for clarity of demonstration, not closing tactics.
        </p>
        <p className="mt-3" style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.7 }}>
          He built The Sewing Haven because he was tired of watching customers leave the shop with a fixed machine and
          no explanation. The Haven is the missing layer between the technician's bench and the educator's camera —
          structured, specific, on Daniel's framework, available at 9pm on a Sunday when the bobbin is fighting you.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <span className="haven-bubble">30 years on the bench</span>
          <span className="haven-bubble" style={{ background: "var(--sage-light)" }}>Every major brand</span>
          <span className="haven-bubble" style={{ background: "var(--rose-light)" }}>A-Best Sew &amp; Vac · 150 yrs family</span>
        </div>
      </div>
    </div>
  </section>
);

const FoundingOffer = () => {
  const [counts, setCounts] = useState({ paid_founders: 0, seats_remaining: 500, total_leads: 0 });
  useEffect(() => {
    api.get("/founders/count").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  return (
    <section data-testid="founding-section" className="haven-page-shell-wide" style={{ paddingTop: 48, paddingBottom: 48 }}>
      <div className="haven-card haven-card-gold" style={{ padding: 28 }}>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <SectionLabel>Founding Round · price moves backwards once</SectionLabel>
            <h2 className="font-serif mt-3" style={{ fontSize: "clamp(28px, 3.4vw, 38px)", lineHeight: 1.1, color: "var(--ink)" }}>
              Fund the build. Lock the rate. Forever.
            </h2>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="haven-label">Cohort</div>
            <div className="font-serif" style={{ fontSize: 28, color: "var(--ink)", lineHeight: 1 }}>
              {counts.seats_remaining}
              <span style={{ fontSize: 14, color: "var(--ink-muted)" }}> / 500 seats remain</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mt-6">
        {TIERS.map((t, i) => (
          <div
            key={t.key}
            data-testid={`tier-card-${t.key}`}
            className={`haven-card fade-up ${t.highlight ? "" : ""}`}
            style={{
              padding: 26,
              border: t.highlight ? "2px solid var(--gold)" : "1px solid var(--border-soft)",
              boxShadow: t.highlight ? "0 12px 30px -16px rgba(181,136,56,0.4)" : "var(--shadow-soft)",
              position: "relative",
            }}
          >
            {t.highlight && (
              <span
                style={{
                  position: "absolute",
                  top: -12,
                  left: 22,
                  background: "var(--gold)",
                  color: "var(--warm-white)",
                  padding: "4px 12px",
                  borderRadius: 999,
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                Most chosen
              </span>
            )}
            <div className="haven-label">{t.label}</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-serif" style={{ fontSize: 48, color: "var(--ink)", lineHeight: 1 }}>
                ${t.price}
              </span>
              <span style={{ color: "var(--ink-muted)", fontSize: 13 }}>{t.cadence}</span>
            </div>
            <div style={{ marginTop: 4, color: "var(--ink-muted)", fontSize: 12 }}>
              <s>${t.standard}{t.cadence}</s> standard ·{" "}
              <span style={{ color: "var(--gold)" }}>{t.savings}</span>
            </div>
            <p style={{ marginTop: 14, color: "var(--ink-soft)", fontSize: 13.5, lineHeight: 1.6 }}>{t.pitch}</p>
            <Link
              to={`/join?tier=${t.key}`}
              data-testid={`tier-cta-${t.key}`}
              className={`mt-5 haven-btn ${t.highlight ? "haven-btn-gold" : "haven-btn-primary"}`}
              style={{ width: "100%" }}
            >
              Lock {t.label.replace("Founding ", "")} rate
            </Link>
            <ul className="mt-5 space-y-2" style={{ color: "var(--ink-soft)", fontSize: 13 }}>
              <li>· All 16 modules as they ship</li>
              <li>· 5+ new videos every month</li>
              <li>· Founding rate locked permanently</li>
              <li>· Direct access to Daniel during build</li>
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

const Faq = () => (
  <section data-testid="faq-section" className="haven-page-shell" style={{ paddingTop: 48, paddingBottom: 48 }}>
    <SectionLabel>Honest Answers</SectionLabel>
    <h2 className="font-serif mt-3" style={{ fontSize: "clamp(28px, 3.4vw, 38px)", lineHeight: 1.1, color: "var(--ink)" }}>
      What founders ask first.
    </h2>
    <div className="mt-7 space-y-3">
      {FAQ.map((f, i) => (
        <details
          key={i}
          data-testid={`faq-item-${i}`}
          className="haven-card"
          style={{ padding: "16px 20px" }}
        >
          <summary
            style={{
              cursor: "pointer",
              listStyle: "none",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
            }}
          >
            <span className="font-serif" style={{ fontSize: 19, color: "var(--ink)" }}>{f.q}</span>
            <span aria-hidden="true" style={{ color: "var(--gold)", fontSize: 18 }}>＋</span>
          </summary>
          <p style={{ marginTop: 10, color: "var(--ink-soft)", fontSize: 14, lineHeight: 1.7 }}>{f.a}</p>
        </details>
      ))}
    </div>
  </section>
);

const FinalCta = () => (
  <section data-testid="final-cta-section" className="haven-page-shell" style={{ paddingTop: 32, paddingBottom: 64 }}>
    <div className="haven-card" style={{ padding: 32, background: "var(--ink)", color: "var(--warm-white)", borderColor: "var(--ink)" }}>
      <SectionLabel>Two ways in</SectionLabel>
      <h2 className="font-serif mt-3" style={{ fontSize: "clamp(28px, 3.6vw, 42px)", lineHeight: 1.1, color: "var(--warm-white)" }}>
        Buy a seat. Or borrow the framework first.
      </h2>
      <p style={{ marginTop: 12, color: "#D5C9B5", fontSize: 14, lineHeight: 1.7, maxWidth: 620 }}>
        Try the free tension diagnostic and the Confidence Snapshot. They'll tell you the same thing the founding round
        does — that there is a layer of teaching missing from your sewing life. The only difference is whether you wait
        to fix it.
      </p>
      <div className="mt-7 flex flex-wrap gap-3">
        <Link to="/join" className="haven-btn haven-btn-gold" data-testid="final-cta-join">
          Lock my founding rate
        </Link>
        <Link to="/diagnose" className="haven-btn haven-btn-ghost" data-testid="final-cta-diagnose" style={{ background: "transparent", color: "var(--warm-white)", borderColor: "rgba(251,248,242,0.3)" }}>
          Try the diagnostic free
        </Link>
        <Link to="/embroidery-cost" className="haven-btn haven-btn-ghost" data-testid="final-cta-calculator" style={{ background: "transparent", color: "var(--warm-white)", borderColor: "rgba(251,248,242,0.3)" }}>
          Embroiderer? See your annual loss
        </Link>
      </div>
      <div className="haven-divider-grain" style={{ margin: "28px 0", background: "rgba(251,248,242,0.12)" }} />
      <div className="grid md:grid-cols-2 gap-6 items-end">
        <div>
          <div className="haven-label" style={{ color: "var(--gold-light)" }}>Or just join the list</div>
          <p style={{ marginTop: 8, color: "#D5C9B5", fontSize: 13, lineHeight: 1.6 }}>
            5 emails over 7 days. The first principles I teach every tech I've ever trained. No pitch until day 7.
          </p>
        </div>
        <LeadMagnetForm
          source="landing-final"
          ctaLabel="Send me the 5-email primer"
          helper="No spam. Unsubscribe in one click."
          testIdPrefix="final-lead"
        />
      </div>
    </div>
  </section>
);

export default function Landing() {
  // First-touch attribution capture — runs on mount, persists for the session.
  useEffect(() => {
    captureAndGetUtm();
  }, []);

  return (
    <main data-testid="landing-page">
      <Hero />
      <Problem />
      <Modules />
      <Daniel />
      <FoundingOffer />
      <Faq />
      <FinalCta />
      <ExitIntentModal source="landing" />
    </main>
  );
}
